import { Alert, Button, CircularProgress } from '@mui/material';
import parsePhoneNumber from 'libphonenumber-js';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useUser from '../../../hooks/useUser';
import { PhoneInput, PhoneInputData } from '../../PhoneInput/PhoneInput';
import { OtpTriggerSource, UserPhoneVerificationType } from '../UserPhoneVerificationModal';
import { useUserVerificationSendCode } from '../useUserVerificationSendCode';

type UserVerificationSendCodeProps = {
  type: UserPhoneVerificationType;
  phone: PhoneInputData;
  setPhone: (phone: PhoneInputData) => void;
  onSuccess: () => void;
  prefillPhoneNumber?: string;
  triggerSource?: OtpTriggerSource;
};

export const UserPhoneVerificationSendCode = ({
  type,
  phone,
  setPhone,
  prefillPhoneNumber,
  onSuccess,
  triggerSource,
}: UserVerificationSendCodeProps) => {
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const phonePrefillInitDoneRef = useRef(false);

  const { t } = useTranslation();
  const { user } = useUser();
  const { onSubmit, isLoading, isCtaDisabled, isPhoneError, setPhoneError, responseError, setResponseError } = useUserVerificationSendCode({
    onSuccess,
    phoneInputRef,
  });

  const formatNumber = (phoneNumber: PhoneInputData) =>
    `+${phoneNumber?.parsed?.countryCallingCode} ${phoneNumber?.parsed?.nationalNumber}`;

  const title = {
    [UserPhoneVerificationType.CHANGE_PHONE_VERIFY_CURRENT_NUMBER]: t('components.phone-verification.edit-phone-confirm-current'),
    [UserPhoneVerificationType.CHANGE_PHONE_VERIFY_NEW_NUMBER]: t('components.phone-verification.edit-phone-confirm-new'),
    [UserPhoneVerificationType.VERIFY_NEW_PHONE]: t('components.phone-verification.title'),
    [UserPhoneVerificationType.VERIFY_ACTION]: t('components.phone-verification.verify-with-phone'),
  }[type];

  const instructions = {
    [UserPhoneVerificationType.CHANGE_PHONE_VERIFY_CURRENT_NUMBER]: t('components.phone-verification.edit-phone-instructions', {
      phone: formatNumber(phone),
    }),
    [UserPhoneVerificationType.CHANGE_PHONE_VERIFY_NEW_NUMBER]: t('components.phone-verification.instructions'),
    [UserPhoneVerificationType.VERIFY_NEW_PHONE]: t(
      triggerSource === 'listing' ? 'components.phone-verification.listing-instructions' : 'components.phone-verification.instructions',
    ),
    [UserPhoneVerificationType.VERIFY_ACTION]: t('components.phone-verification.verify-with-phone-instructions', {
      phone: formatNumber(phone),
    }),
  }[type];

  // Prefill initial phone number.
  useEffect(() => {
    if (user?.phone && !phonePrefillInitDoneRef.current) {
      phonePrefillInitDoneRef.current = true;

      setPhone({
        raw: user?.phone,
        parsed: parsePhoneNumber(prefillPhoneNumber || user?.phone),
      });
    }
  }, [user]);

  return (
    <>
      <h2 className="user-verification-modal-title">{title}</h2>

      <div className="user-verification-modal-body pt-1">
        <span dangerouslySetInnerHTML={{ __html: instructions }} />

        {[UserPhoneVerificationType.CHANGE_PHONE_VERIFY_NEW_NUMBER, UserPhoneVerificationType.VERIFY_NEW_PHONE].includes(type) && (
          <PhoneInput
            inputRef={phoneInputRef}
            value={phone?.raw ?? ''}
            onChange={(p) => {
              setResponseError('');
              setPhoneError(false);
              setPhone(p);
            }}
            fullWidth
            className="mt-6"
            error={isPhoneError}
            helperText={isPhoneError && t('components.phone-verification.incorrect-number')}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                onSubmit(phone);
              }
            }}
            disabled={isLoading}
            countryCodeDisabled={isLoading}
          />
        )}

        {responseError && (
          <Alert severity="error" variant="standard" className="mt-1-i color-danger w-100">
            {responseError}
          </Alert>
        )}

        {/* Actions */}
        <div className="user-verification-modal-actions mt-4">
          <Button
            variant="contained"
            color="primary"
            onClick={() => onSubmit(phone)}
            fullWidth
            size="large"
            disabled={isLoading || isCtaDisabled}
            startIcon={isLoading && <CircularProgress color="inherit" size={16} />}
          >
            {t('common.continue')}
          </Button>
        </div>
      </div>
    </>
  );
};
